import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FileCopy as FileCopyIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  Info,
} from '@mui/icons-material';
import { apiTokensAPI } from '../services/api';
import { toast } from 'react-toastify';
import Accordion from './UIKit/Accordion';
import Button from './UIKit/Button';
import TextField from './UIKit/TextField';
import { useTranslation } from 'react-i18next';

const ApiTokenManager = () => {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);
  const [newToken, setNewToken] = useState({
    name: '',
    description: '',
    permissions: ['course:save'],
  });
  const [recentlyCreatedToken, setRecentlyCreatedToken] = useState(null);
  const [showTokenValue, setShowTokenValue] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка токенов
  const loadTokens = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiTokensAPI.getAll();
      setTokens(response.data.data);
    } catch (err) {
      console.error(t('apiTokens.errors.loadError'), err);
      setError(t('apiTokens.errors.loadErrorMessage'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Обработчики формы создания токена
  const handleCreateDialogOpen = () => {
    setOpenCreateDialog(true);
    setRecentlyCreatedToken(null);
  };

  const handleCreateDialogClose = () => {
    setOpenCreateDialog(false);
    setNewToken({
      name: '',
      description: '',
      permissions: ['course:save'],
    });
  };

  const handleCreateToken = async () => {
    try {
      if (!newToken.name.trim()) {
        toast.error(t('apiTokens.validation.nameRequired'));
        return;
      }

      const response = await apiTokensAPI.create(newToken);
      toast.success(t('apiTokens.success.created'));

      setRecentlyCreatedToken(response.data.data);
      await loadTokens();
    } catch (err) {
      console.error(t('apiTokens.errors.createError'), err);
      toast.error(t('apiTokens.errors.createErrorMessage'));
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return t('apiTokens.status.neverUsed');
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Копирование токена в буфер обмена
  const copyToClipboard = (token) => {
    navigator.clipboard
      .writeText(token)
      .then(() => toast.success(t('apiTokens.success.copied')))
      .catch((err) => {
        console.error(t('apiTokens.errors.copyError'), err);
        toast.error(t('apiTokens.errors.copyErrorMessage'));
      });
  };

  // Деактивация токена
  const handleDeactivateToken = async (id) => {
    try {
      await apiTokensAPI.deactivate(id);
      toast.success(t('apiTokens.success.deactivated'));
      await loadTokens();
    } catch (err) {
      console.error(t('apiTokens.errors.deactivateError'), err);
      toast.error(t('apiTokens.errors.deactivateErrorMessage'));
    }
  };

  // Активация токена
  const handleActivateToken = async (id) => {
    try {
      await apiTokensAPI.activate(id);
      toast.success(t('apiTokens.success.activated'));
      await loadTokens();
    } catch (err) {
      console.error(t('apiTokens.errors.activateError'), err);
      toast.error(t('apiTokens.errors.activateErrorMessage'));
    }
  };

  // Удаление токена - диалог подтверждения
  const handleDeleteDialogOpen = (token) => {
    setTokenToDelete(token);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setTokenToDelete(null);
  };

  const handleDeleteToken = async () => {
    if (!tokenToDelete) return;

    try {
      await apiTokensAPI.delete(tokenToDelete._id);
      toast.success(t('apiTokens.success.deleted'));
      await loadTokens();
      handleDeleteDialogClose();
    } catch (err) {
      console.error(t('apiTokens.errors.deleteError'), err);
      toast.error(t('apiTokens.errors.deleteErrorMessage'));
    }
  };

  const toggleTokenVisibility = () => {
    setShowTokenValue(!showTokenValue);
  };

  // Маскирование токена для защиты
  const maskToken = (token) => {
    if (!token) return '';
    if (showTokenValue) return token;

    if (token.length <= 8) return '••••••••';
    return token.substr(0, 4) + '•'.repeat(token.length - 8) + token.substr(-4);
  };

  // Рендер таблиці токенів у стилі CategoryManagement.js
  const renderTokensTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      );
    }

    if (tokens.length === 0) {
      // Відображаємо порожню таблицю як у CategoryManagement.js
      return (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
          <Info color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">{t('apiTokens.empty.title')}</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('apiTokens.empty.description')}
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('apiTokens.table.name')}</TableCell>
              <TableCell>{t('apiTokens.table.token')}</TableCell>
              <TableCell>{t('apiTokens.table.status')}</TableCell>
              <TableCell>{t('apiTokens.table.created')}</TableCell>
              <TableCell>{t('apiTokens.table.lastUsed')}</TableCell>
              <TableCell align="right">
                {t('apiTokens.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map((token) => (
              <TableRow key={token._id}>
                <TableCell>
                  <Typography variant="body1">{token.name}</Typography>
                  {token.description && (
                    <Typography variant="caption" color="textSecondary">
                      {token.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: '200px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        mr: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {maskToken(token.token)}
                    </Typography>
                    <Tooltip title={t('apiTokens.actions.copy')}>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(token.token)}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      token.isActive
                        ? t('apiTokens.status.active')
                        : t('apiTokens.status.inactive')
                    }
                    color={token.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(token.createdAt)}</TableCell>
                <TableCell>{formatDate(token.lastUsedAt)}</TableCell>
                <TableCell align="right">
                  {/* Дії вирівнюємо як у CategoryManagement.js */}
                  <Box
                    sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                  >
                    {token.isActive ? (
                      <Tooltip title={t('apiTokens.actions.deactivate')}>
                        <IconButton
                          color="warning"
                          onClick={() => handleDeactivateToken(token._id)}
                        >
                          <ToggleOnIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('apiTokens.actions.activate')}>
                        <IconButton
                          color="success"
                          onClick={() => handleActivateToken(token._id)}
                        >
                          <ToggleOffIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('apiTokens.actions.delete')}>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteDialogOpen(token)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="h5" component="h2">
          {t('apiTokens.title')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTokens}
            sx={{ mr: 1 }}
          >
            {t('apiTokens.actions.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateDialogOpen}
          >
            {t('apiTokens.actions.create')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {t('apiTokens.description')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t('apiTokens.important')}:</strong>{' '}
          {t('apiTokens.securityWarning')}
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={toggleTokenVisibility}
            color="primary"
            size="small"
            sx={{ mr: 1 }}
          >
            {showTokenValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {showTokenValue
              ? t('apiTokens.visibility.hide')
              : t('apiTokens.visibility.show')}
          </Typography>
        </Box>
      </Box>

      {renderTokensTable()}

      {/* Документация по API */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
          {t('apiTokens.docs.title')}
        </Typography>

        <Accordion elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {t('apiTokens.docs.authentication.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('apiTokens.docs.authentication.description')}
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                fontFamily: 'monospace',
                mb: 3,
              }}
            >
              <Typography variant="body2" color="text.primary">
                X-API-Token: {t('apiTokens.docs.authentication.yourToken')}
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                {t('apiTokens.docs.authentication.or')}
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Authorization: Bearer{' '}
                {t('apiTokens.docs.authentication.yourToken')}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {t('apiTokens.docs.importCourse.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('apiTokens.docs.importCourse.description')}
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                mb: 3,
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {`POST /api/course
Content-Type: application/json
X-API-Token: ${t('apiTokens.docs.authentication.yourToken')}

{
  "title": "${t('apiTokens.docs.importCourse.courseName')}",
  "description": "${t('apiTokens.docs.importCourse.courseDescription')}",
  "category": "${t('apiTokens.docs.importCourse.categoryId')}",
  "difficulty": "beginner",
  "modules": [
    {
      "title": "${t('apiTokens.docs.importCourse.moduleName')}",
      "description": "${t('apiTokens.docs.importCourse.moduleDescription')}",
      "content": "${t('apiTokens.docs.importCourse.moduleContent')}",
      "lessons": [...]
    }
  ]
}`}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              {t('apiTokens.docs.batchImport.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('apiTokens.docs.batchImport.description')}
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {`POST /api/courses/batch
Content-Type: application/json
X-API-Token: ${t('apiTokens.docs.authentication.yourToken')}

[
  {
    "title": "${t('apiTokens.docs.batchImport.course1')}",
    "description": "${t('apiTokens.docs.batchImport.description1')}",
    ...
  },
  {
    "title": "${t('apiTokens.docs.batchImport.course2')}",
    "description": "${t('apiTokens.docs.batchImport.description2')}",
    ...
  }
]`}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Диалоги (существующий код) */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: '16px' } }}
      >
        <DialogTitle>
          <Typography variant="h6">{t('apiTokens.create.title')}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {recentlyCreatedToken ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('apiTokens.create.successMessage')}
              </Alert>
              <Typography variant="subtitle2" gutterBottom>
                {t('apiTokens.create.tokenLabel')}:
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ wordBreak: 'break-all' }}>
                  {recentlyCreatedToken.token}
                </Typography>
                <IconButton
                  onClick={() => copyToClipboard(recentlyCreatedToken.token)}
                  color="primary"
                >
                  <FileCopyIcon />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              <TextField
                autoFocus
                margin="dense"
                label={t('apiTokens.create.nameLabel')}
                type="text"
                fullWidth
                variant="outlined"
                value={newToken.name}
                onChange={(e) =>
                  setNewToken({ ...newToken, name: e.target.value })
                }
                sx={{ mb: 2, mt: 1 }}
              />
              <TextField
                margin="dense"
                label={t('apiTokens.create.descriptionLabel')}
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                value={newToken.description}
                onChange={(e) =>
                  setNewToken({ ...newToken, description: e.target.value })
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCreateDialogClose} variant="outlined">
            {recentlyCreatedToken
              ? t('common:actions.close')
              : t('common:actions.cancel')}
          </Button>
          {!recentlyCreatedToken && (
            <Button
              onClick={handleCreateToken}
              variant="contained"
              color="primary"
            >
              {t('apiTokens.create.createButton')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: '16px' } }}
      >
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Box
            sx={{
              bgcolor: 'error.main',
              width: 64,
              height: 64,
              borderRadius: '50%',
              margin: '0 auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DeleteIcon sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ mb: 1, fontSize: '1.25rem', fontWeight: 600 }}
          >
            {t('apiTokens.delete.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('apiTokens.delete.confirmation', { name: tokenToDelete?.name })}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={handleDeleteDialogClose}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              onClick={handleDeleteToken}
              color="error"
              variant="contained"
              sx={{ minWidth: 100 }}
            >
              {t('common:actions.delete')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ApiTokenManager;
